import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box, Typography, Button, TextField, Select, MenuItem, FormControl, InputLabel,
  useMediaQuery, Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SendIcon from '@mui/icons-material/Send';
import { api } from '../../api/api';
import { addChangeRequest, getChangeRequests } from '../../Redux/Actions/changeRequestAction';

// HOT: 12oz(hot_s), 16oz(hot_m)  |  ICED: 16oz(ice_s), 20oz(ice_m)
const SIZE_KEYS = ['hot_s', 'hot_m', 'ice_s', 'ice_m'];
const SIZE_LABELS = { hot_s: 'HOT 12oz', hot_m: 'HOT 16oz', ice_s: 'ICED 16oz', ice_m: 'ICED 20oz' };
const SIZE_SHORT = { hot_s: '12oz', hot_m: '16oz', ice_s: '16oz', ice_m: '20oz' };
const CATS = [
  { key: 'espresso', label: 'ESPRESSO' },
  { key: 'brewed_coffee', label: 'BREWED' },
  { key: 'tea', label: 'TEA' },
  { key: 'cold_drink', label: 'COLD DRINKS' },
];

function PriceRequestForm({ store, userId, user, subuserId, onSubmitted }) {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width:600px)');
  const [menuPrices, setMenuPrices] = useState([]);
  const [menuMasters, setMenuMasters] = useState([]);
  const [priceEdits, setPriceEdits] = useState({});
  const [activeCat, setActiveCat] = useState('espresso');
  const [showForm, setShowForm] = useState(false);
  const [showNewMenu, setShowNewMenu] = useState(false);
  const [newMenuName, setNewMenuName] = useState('');
  const [newMenuCat, setNewMenuCat] = useState('espresso');
  const [submitting, setSubmitting] = useState(false);

  const loadData = useCallback(async () => {
    if (!store?._id) return;
    try {
      const [pr, mm] = await Promise.all([
        api.get(`/menu-prices/${store._id}`),
        api.get('/menu-master/items'),
      ]);
      setMenuPrices(pr.data || []);
      setMenuMasters(mm.data?.items || mm.data || []);
    } catch (e) { console.error(e); }
  }, [store?._id]);

  useEffect(() => { if (showForm) loadData(); }, [showForm, loadData]);

  const handleEdit = (menuCode, sizeKey, value) => {
    const cents = Math.round(parseFloat(value || 0) * 100);
    setPriceEdits(prev => ({ ...prev, [menuCode]: { ...(prev[menuCode] || {}), [sizeKey]: cents } }));
  };

  const changeSummary = Object.entries(priceEdits).flatMap(([code, sizes]) => {
    const mp = menuPrices.find(m => m.menuCode === code);
    if (!mp) return [];
    return Object.entries(sizes).filter(([k, v]) => v !== (mp.prices?.[k] || 0) && v > 0)
      .map(([k, v]) => ({ menuCode: code, menuName: mp.menuName, sizeKey: k, oldPrice: mp.prices?.[k] || 0, newPrice: v }));
  });

  const submitChanges = async () => {
    if (changeSummary.length === 0 && !showNewMenu) return;
    setSubmitting(true);

    if (changeSummary.length > 0) {
      const details = changeSummary.map(c =>
        `${c.menuName} ${SIZE_LABELS[c.sizeKey]}: $${(c.oldPrice / 100).toFixed(2)} → $${(c.newPrice / 100).toFixed(2)}`
      ).join('\n');
      await dispatch(addChangeRequest({
        user: userId, store: store._id,
        requestedBy: subuserId || undefined,
        requestedByName: user?.username || '',
        type: 'price_change',
        changeDetails: `가격 변경 요청 (${changeSummary.length}건):\n${details}`,
        priceChanges: changeSummary,
      }));
    }

    setPriceEdits({});
    setShowForm(false);
    setSubmitting(false);
    if (onSubmitted) onSubmitted();
    dispatch(getChangeRequests(userId, { subuserId }));
  };

  const submitNewMenu = async () => {
    if (!newMenuName.trim()) return;
    setSubmitting(true);
    await dispatch(addChangeRequest({
      user: userId, store: store._id,
      requestedBy: subuserId || undefined,
      requestedByName: user?.username || '',
      type: 'new_menu', menuName: newMenuName,
      changeDetails: `신규 메뉴 추가 요청: ${newMenuName} (${newMenuCat})`,
    }));
    setNewMenuName('');
    setShowNewMenu(false);
    setSubmitting(false);
    if (onSubmitted) onSubmitted();
    dispatch(getChangeRequests(userId, { subuserId }));
  };

  const catMenus = menuPrices.filter(mp => {
    const m = menuMasters.find(mm => mm.code === mp.menuCode);
    return m?.category === activeCat;
  }).sort((a, b) => {
    const ma = menuMasters.find(m => m.code === a.menuCode);
    const mb = menuMasters.find(m => m.code === b.menuCode);
    return (ma?.order || 0) - (mb?.order || 0);
  });

  const fmt = (cents) => (cents / 100).toFixed(2);

  return (
    <Box sx={{ mb: 2 }}>
      {/* Toggle */}
      <Button fullWidth variant="outlined"
        onClick={() => setShowForm(!showForm)}
        endIcon={showForm ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ borderColor: '#1B5E3B', color: '#1B5E3B', borderRadius: 2, mb: 1, justifyContent: 'space-between', textTransform: 'none', fontWeight: 600 }}>
        {showForm ? '가격 변경 폼 닫기' : '📋 메뉴 가격 변경 요청하기'}
      </Button>

      <Collapse in={showForm}>
        <Box sx={{ border: '1px solid #C8E6C9', borderRadius: 3, overflow: 'hidden', background: '#fff' }}>
          {/* Category pills */}
          <Box sx={{ display: 'flex', gap: 0.8, p: '10px 12px', overflowX: 'auto', background: '#f8f8f8', borderBottom: '1px solid #e8e8e8', '&::-webkit-scrollbar': { display: 'none' } }}>
            {CATS.map(c => (
              <Box key={c.key} onClick={() => setActiveCat(c.key)} sx={{
                flexShrink: 0, px: 1.5, py: 0.6, borderRadius: '16px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                background: activeCat === c.key ? '#1B5E3B' : '#eee', color: activeCat === c.key ? '#fff' : '#888',
              }}>{c.label}</Box>
            ))}
          </Box>

          {/* Menu items - compact */}
          <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
            {catMenus.map(mp => {
              const edits = priceEdits[mp.menuCode] || {};
              const hasChange = Object.entries(edits).some(([k, v]) => v !== (mp.prices?.[k] || 0) && v > 0);

              if (isMobile) {
                // Mobile: card layout
                return (
                  <Box key={mp._id} sx={{ p: '10px 14px', borderBottom: '1px solid #f0f0f0', background: hasChange ? '#FFFDE7' : '#fff' }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.8 }}>{mp.menuName}</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(70px, 1fr))', gap: '6px' }}>
                      {SIZE_KEYS.filter(k => mp.prices?.[k] !== undefined).map(k => {
                        const isHot = k.startsWith('hot');
                        const current = mp.prices[k];
                        const edited = edits[k];
                        const changed = edited !== undefined && edited !== current && edited > 0;
                        return (
                          <Box key={k} sx={{ textAlign: 'center' }}>
                            <Typography sx={{ fontSize: 9, fontWeight: 700, color: isHot ? '#C62828' : '#1565C0', mb: 0.3 }}>
                              {isHot ? '☕' : '🧊'} {SIZE_SHORT[k]}
                            </Typography>
                            <input type="number" step="0.05"
                              defaultValue={fmt(current)}
                              onBlur={e => handleEdit(mp.menuCode, k, e.target.value)}
                              style={{
                                width: '100%', padding: '6px 4px', border: `1.5px solid ${changed ? '#C5A55A' : '#e0e0e0'}`,
                                borderRadius: 6, textAlign: 'center', fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
                                background: changed ? '#F5EDD6' : '#fafafa',
                              }} />
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                );
              }

              // Desktop: table row
              return (
                <Box key={mp._id} sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, borderBottom: '1px solid #f0f0f0', background: hasChange ? '#FFFDE7' : '#fff', gap: 1.5 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 500, minWidth: 140, flex: '0 0 140px' }}>{mp.menuName}</Typography>
                  {SIZE_KEYS.map(k => {
                    const price = mp.prices?.[k];
                    const isHot = k.startsWith('hot');
                    if (price === undefined) return <Box key={k} sx={{ flex: 1, textAlign: 'center', color: '#ddd', fontSize: 12 }}>—</Box>;
                    const edited = edits[k];
                    const changed = edited !== undefined && edited !== price && edited > 0;
                    return (
                      <Box key={k} sx={{ flex: 1, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 9, fontWeight: 700, color: isHot ? '#C62828' : '#1565C0' }}>
                          {isHot ? '☕' : '🧊'} {SIZE_SHORT[k]}
                        </Typography>
                        <input type="number" step="0.05"
                          defaultValue={fmt(price)}
                          onBlur={e => handleEdit(mp.menuCode, k, e.target.value)}
                          style={{
                            width: 64, padding: '4px 6px', border: `1px solid ${changed ? '#C5A55A' : '#ddd'}`,
                            borderRadius: 4, textAlign: 'center', fontSize: 12, fontFamily: 'inherit',
                            background: changed ? '#F5EDD6' : '#fafafa',
                          }} />
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Box>

          {/* Change summary + submit */}
          {changeSummary.length > 0 && (
            <Box sx={{ p: 2, background: '#FDF8F0', borderTop: '2px solid #C5A55A' }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#5D4037', mb: 1 }}>
                변경 요약 ({changeSummary.length}건)
              </Typography>
              {changeSummary.map((c, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, fontSize: 12 }}>
                  <span>{c.menuName} <span style={{ color: '#888' }}>{SIZE_LABELS[c.sizeKey].split(' ')[1]}</span></span>
                  <span>
                    <span style={{ color: '#C62828', textDecoration: 'line-through' }}>${fmt(c.oldPrice)}</span>
                    <span style={{ color: '#C5A55A', margin: '0 4px' }}>→</span>
                    <span style={{ color: '#1B5E3B', fontWeight: 700 }}>${fmt(c.newPrice)}</span>
                  </span>
                </Box>
              ))}
              <Box sx={{ display: 'flex', gap: 1, mt: 1.5, justifyContent: 'flex-end' }}>
                <Button size="small" onClick={() => setPriceEdits({})} sx={{ color: '#888', fontSize: 12 }}>초기화</Button>
                <Button size="small" variant="contained" onClick={submitChanges} disabled={submitting}
                  startIcon={<SendIcon sx={{ fontSize: 14 }} />}
                  sx={{ background: '#1B5E3B', fontSize: 12, borderRadius: 2, '&:hover': { background: '#2D8B5E' } }}>
                  {submitting ? '제출 중...' : '변경 요청 제출'}
                </Button>
              </Box>
            </Box>
          )}

          {/* New menu quick add */}
          <Box sx={{ p: '10px 14px', borderTop: '1px dashed #e0e0e0', display: 'flex', gap: 1, alignItems: 'center', background: '#fafafa' }}>
            <TextField size="small" placeholder="신규 메뉴 이름" value={newMenuName}
              onChange={e => setNewMenuName(e.target.value)}
              sx={{ flex: 1 }} inputProps={{ style: { fontSize: 13 } }} />
            <Button size="small" variant="contained" disabled={!newMenuName.trim() || submitting}
              onClick={submitNewMenu}
              sx={{ background: '#E65100', fontSize: 11, borderRadius: 2, whiteSpace: 'nowrap', '&:hover': { background: '#F57C00' } }}>
              + 신규 메뉴
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

export default PriceRequestForm;
